import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BRAND } from "@/lib/site";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const localBusinessLd = {
  "@type": "LocalBusiness",
  "@id": `${BRAND.origin}/#business`,
  name: BRAND.name,
  description:
    "Bremer Schneemann ist Ihr Winterdienst in Bremen und Umgebung: Schneeräumung, Streudienst und Eisbeseitigung für private und gewerbliche Objekte.",
  url: BRAND.origin,
  telephone: BRAND.phone,
  email: BRAND.email,
  image: BRAND.logo,
  logo: BRAND.logo,
  vatID: BRAND.vatId,
  founder: { "@type": "Person", name: BRAND.owner },
  address: {
    "@type": "PostalAddress",
    streetAddress: BRAND.street,
    postalCode: BRAND.zip,
    addressLocality: BRAND.city,
    addressCountry: "DE",
  },
  areaServed: [
    { "@type": "City", name: "Bremen" },
    { "@type": "City", name: "Delmenhorst" },
    { "@type": "City", name: "Stuhr" },
    { "@type": "City", name: "Weyhe" },
    { "@type": "City", name: "Achim" },
    { "@type": "City", name: "Oyten" },
    { "@type": "City", name: "Lilienthal" },
    { "@type": "City", name: "Ritterhude" },
    { "@type": "City", name: "Schwanewede" },
    { "@type": "City", name: "Lemwerder" },
    { "@type": "City", name: "Ganderkesee" },
    { "@type": "City", name: "Osterholz-Scharmbeck" },
  ],
  priceRange: "Angebot auf Anfrage",
};

const Seo = ({
  title,
  description,
  path,
  breadcrumbs = [],
  faq = [],
  service,
  image = BRAND.logo,
}) => {
  const location = useLocation();
  const url = `${BRAND.origin}${path || location.pathname}`;

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = "de";
    setMeta("name", "description", description);
    setMeta("name", "robots", "index,follow,max-image-preview:large");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:site_name", BRAND.name);
    setMeta("property", "og:locale", "de_DE");
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    setMeta("name", "geo.placename", "Bremen");
    setLink("canonical", url);

    const graph = [
      {
        "@type": "WebSite",
        "@id": `${BRAND.origin}/#website`,
        url: BRAND.origin,
        name: BRAND.name,
        inLanguage: "de-DE",
        publisher: { "@id": `${BRAND.origin}/#business` },
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: title,
        description,
        inLanguage: "de-DE",
        isPartOf: { "@id": `${BRAND.origin}/#website` },
        about: { "@id": `${BRAND.origin}/#business` },
        primaryImageOfPage: image,
      },
      {
        "@type": "Person",
        "@id": `${BRAND.origin}/#owner`,
        name: BRAND.owner,
        jobTitle: "Inhaber",
        worksFor: { "@id": `${BRAND.origin}/#business` },
        telephone: BRAND.phone,
        email: BRAND.email,
      },
      localBusinessLd,
    ];

    if (breadcrumbs.length) {
      graph.push({
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.label,
          item: `${BRAND.origin}${b.to}`,
        })),
      });
    }

    if (service) {
      graph.push({
        "@type": "Service",
        name: service,
        serviceType: service,
        provider: { "@id": `${BRAND.origin}/#business` },
        areaServed: { "@type": "City", name: "Bremen" },
        url,
      });
    }

    if (faq.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }

    let script = document.getElementById("ld-json");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "ld-json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
  }, [title, description, url, image, JSON.stringify(breadcrumbs), JSON.stringify(faq), service]);

  return null;
};

export default Seo;
